import { inject, Injectable, signal } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { AuthService } from './auth.service';
import {
  CambioTurnoAuditoria,
  CambioTurnoPendiente,
  CheckAuditoriaTurno,
  CheckEntregaTurno,
  ConteoRecepcion,
  ResultadoControlTurno,
  SesionAuditoria,
} from '../models/cash-shift.model';
import { normalizarPendiente, normalizarResultadoControl, sesionConActividadEnDia } from '../utils/cash-shift.util';

/**
 * Cambio de turno en caja: control de conteo entre quien entrega y quien recibe, sin cerrar la caja.
 * Las escrituras y el conteo a ciegas van por funciones de la base; la auditoría la leen solo dueño y admin.
 */
@Injectable({ providedIn: 'root' })
export class CashShiftService {
  private readonly supabase = inject(SupabaseService).client as any;
  private readonly auth = inject(AuthService);

  /** Traspaso pendiente de la sucursal activa (null = la caja no está en cambio de turno). */
  readonly pendiente = signal<CambioTurnoPendiente | null>(null);

  /** Refresca el traspaso pendiente de una sucursal. Sin sucursal, o ante un error, queda en null. */
  async cargarPendiente(branchId: string | null): Promise<CambioTurnoPendiente | null> {
    if (!branchId) {
      this.pendiente.set(null);
      return null;
    }
    try {
      const { data, error } = await this.supabase.rpc('get_pending_cash_shift_handover', { p_branch_id: branchId });
      if (error) throw error;
      const pendiente = normalizarPendiente(data);
      this.pendiente.set(pendiente);
      return pendiente;
    } catch (e) {
      console.error('Error al consultar el cambio de turno pendiente:', e);
      this.pendiente.set(null);
      return null;
    }
  }

  /**
   * Entrega la caja: queda en espera hasta que la próxima vendedora valide.
   * @param sellerId Quien entrega (solo rol punto_de_venta; con otros roles es el usuario logueado).
   * @param checks Verificación de cada medio no efectivo (vacío si la empresa no los verifica al cerrar).
   */
  async iniciar(
    sessionId: string,
    sellerId: string | null,
    countedCash: number,
    checks: CheckEntregaTurno[],
    notes: string
  ): Promise<{ error: any }> {
    const { error } = await this.supabase.rpc('start_cash_shift_handover', {
      p_session_id: sessionId,
      p_seller_id: sellerId,
      p_counted_cash: countedCash,
      p_checks: checks,
      p_notes: notes || null,
    });
    return { error };
  }

  /** Valida lo recibido (conteo a ciegas) y devuelve el resultado del control. Libera la caja. */
  async validar(
    handoverId: string,
    sellerId: string | null,
    countedCash: number,
    checks: ConteoRecepcion[],
    notes: string
  ): Promise<{ resultado: ResultadoControlTurno | null; error: any }> {
    const { data, error } = await this.supabase.rpc('validate_cash_shift_handover', {
      p_handover_id: handoverId,
      p_seller_id: sellerId,
      p_counted_cash: countedCash,
      p_checks: checks,
      p_notes: notes || null,
    });
    if (error) return { resultado: null, error };
    return { resultado: normalizarResultadoControl(data), error: null };
  }

  /** Anula un cambio de turno pendiente y libera la caja. Queda en la auditoría. */
  async anular(handoverId: string, reason: string): Promise<{ error: any }> {
    const { error } = await this.supabase.rpc('void_cash_shift_handover', {
      p_handover_id: handoverId,
      p_reason: reason || null,
    });
    return { error };
  }

  /**
   * Auditoría de un día: las cajas de la sucursal con actividad ese día (se abrieron, se cerraron o tuvieron un cambio
   * de turno) con todos sus cambios de turno (validados, anulados y pendientes). Solo dueño, admin y superadmin pueden leer estas tablas.
   */
  async getAuditoriaDia(branchId: string, dia: Date): Promise<SesionAuditoria[]> {
    const businessId = this.auth.userProfile()?.business_id;
    if (!businessId) return [];

    const desde = new Date(dia);
    desde.setHours(0, 0, 0, 0);
    const hasta = new Date(dia);
    hasta.setHours(23, 59, 59, 999);

    const { data: sesiones, error } = await this.supabase
      .from('cash_sessions')
      .select('id, opened_at, closed_at, status, initial_amount, difference, opener:opened_by(full_name), closer:closed_by(full_name), closed_by_seller:closed_by_seller_id(name)')
      .eq('business_id', businessId)
      .eq('branch_id', branchId)
      // Cajas que estuvieron abiertas en algún momento del día (pueden venir de días anteriores).
      .lte('opened_at', hasta.toISOString())
      .or(`closed_at.is.null,closed_at.gte.${desde.toISOString()}`)
      .order('opened_at', { ascending: true });
    if (error) throw error;
    if (!sesiones?.length) return [];

    const { data: cambios, error: errorCambios } = await this.supabase
      .from('cash_shift_handovers')
      .select(`*,
        handed_seller:handed_by_seller_id(name), handed_profile:handed_by_user(full_name),
        received_seller:received_by_seller_id(name), received_profile:received_by_user(full_name),
        voided_profile:voided_by_user(full_name),
        checks:cash_shift_handover_checks(side, method_key, method_name, method_type, system_amount, verified_amount)`)
      .eq('business_id', businessId)
      .in('session_id', sesiones.map((s: any) => s.id))
      .order('initiated_at', { ascending: true });
    if (errorCambios) throw errorCambios;

    const todas = sesiones.map((s: any): SesionAuditoria => ({
      id: s.id,
      opened_at: s.opened_at,
      closed_at: s.closed_at,
      status: s.status,
      initial_amount: Number(s.initial_amount) || 0,
      difference: Number(s.difference) || 0,
      abrio: s.opener?.full_name ?? 'Sin nombre',
      // Con login compartido el cierre lo hace la vendedora elegida; si no, el usuario que cerró.
      cerro: s.status === 'closed' ? (s.closed_by_seller?.name ?? s.closer?.full_name ?? 'Sin nombre') : null,
      turnos: (cambios || []).filter((c: any) => c.session_id === s.id).map(mapearCambio),
    }));

    return todas.filter((s: SesionAuditoria) => sesionConActividadEnDia(s, desde, hasta));
  }
}

function mapearCambio(c: any): CambioTurnoAuditoria {
  const num = (v: unknown): number => Number(v) || 0;
  const numONull = (v: unknown): number | null => (v === null || v === undefined ? null : Number(v));
  return {
    id: c.id,
    session_id: c.session_id,
    status: c.status,
    initiated_at: c.initiated_at,
    validated_at: c.validated_at ?? null,
    voided_at: c.voided_at ?? null,
    void_reason: c.void_reason ?? null,
    expected_cash: num(c.expected_cash),
    counted_cash: num(c.counted_cash),
    received_counted_cash: numONull(c.received_counted_cash),
    cash_difference: num(c.cash_difference),
    received_cash_difference: numONull(c.received_cash_difference),
    handover_notes: c.handover_notes ?? null,
    received_notes: c.received_notes ?? null,
    entrega: c.handed_seller?.name ?? c.handed_profile?.full_name ?? 'Sin nombre',
    recibe: c.received_seller?.name ?? c.received_profile?.full_name ?? null,
    anulo: c.voided_profile?.full_name ?? null,
    checks: ((c.checks || []) as any[]).map((k): CheckAuditoriaTurno => ({
      side: k.side,
      method_key: k.method_key,
      method_name: k.method_name,
      method_type: k.method_type,
      system_amount: num(k.system_amount),
      verified_amount: num(k.verified_amount),
    })),
  };
}
